/* ──────────────────────────────────────────────────────────────────────────
   Per-market site configuration — single source of truth for both builds.

   One codebase produces two sites, selected at build time by the GATSBY_SITE
   environment variable:

       GATSBY_SITE=com   (default)  →  rscapitalglobal.com  (international)
       GATSBY_SITE=ae               →  rscapitalglobal.ae   (UAE / DIFC)

   GATSBY_SITE is used (not plain SITE) so the value is available in BOTH the
   Node build (gatsby-config / gatsby-node) AND the browser bundle.

   Everything that differs between the two sites lives here. Page COPY is not
   extracted wholesale — it stays in the components until a page actually needs
   to differ, at which point that page's content is pulled into a content module
   and resolved via src/utils/resolveContent.js.
   ────────────────────────────────────────────────────────────────────────── */

const SITE = process.env.GATSBY_SITE === 'ae' ? 'ae' : 'com';

// Values common to both sites (a market can still override any of these below).
const shared = {
    title: 'Redstone Capital',
    description:
        'Wealth Management & Financial Planning for Global Expatriates. Bespoke financial advice designed for international professionals.',
    author: 'Redstone Capital',
    email: 'rcadmin@rscapitalglobal.com',
    address: {
        streetAddress: 'P6-01C, Park Towers, DIFC',
        addressLocality: 'Dubai',
        addressCountry: 'AE',
    },
    social: [
        'https://www.linkedin.com/company/redstone-capital-global/',
        'https://www.instagram.com/redstonecapitalglobal/',
        'https://www.facebook.com/people/Redstone-Capital/100024792555780/',
        'https://www.youtube.com/channel/UCpMAx3_YowlODxEsH-V6Eog',
    ],
};

const sites = {
    // ── International site ────────────────────────────────────────────────────
    com: {
        key: 'com',
        siteUrl: 'https://rscapitalglobal.com',
        // GTM: on the .com Netlify site GATSBY_GTM_ID is normally unset, so it
        // falls back to the known live container.
        gtmId: process.env.GATSBY_GTM_ID || 'GTM-N9VZ3TCB',
        gaId: process.env.GATSBY_GA_ID || '',
        legalName: 'Redstone Capital Global',
        geoThisSite: 'com',
        // Regulatory disclosure line shown in the footer (empty = none).
        regulatoryLine: '',
        // Article market. null = show every article regardless of tags (.com is
        // the catch-all). A regional site sets its own market value instead.
        articleMarket: null,
        // Solution section paths NOT offered on this site. Any page whose path
        // starts with one of these is removed at build (see gatsby-node.js) and
        // filtered out of the nav / footer. Empty = every solution is offered.
        disabledSolutions: [],
        // Exact paths removed WITHOUT removing their children (e.g. drop a
        // landing page but keep one sub-page). Prefix children survive.
        disabledExact: [],
        // Where a removed landing is replaced by a promoted child in nav/cards:
        // { '<removed path>': { path: '<child path>', name: '<label>' } }.
        promotions: {},
    },

    // ── UAE / DIFC site ───────────────────────────────────────────────────────
    ae: {
        key: 'ae',
        siteUrl: 'https://rscapitalglobal.ae',
        // .ae GTM container (RS Capital Global account). Env var can override.
        gtmId: process.env.GATSBY_GTM_ID || 'GTM-T26J68VK',
        gaId: process.env.GATSBY_GA_ID || '',
        legalName: 'Redstone Capital Global', // DIFC entity — compliance to confirm
        geoThisSite: 'ae',
        // Regulatory disclosure required on the UAE / DIFC site.
        regulatoryLine: 'Regulated by the DFSA',
        // This site's value in the Contentful "Markets" field.
        articleMarket: 'UAE',
        // Services not offered under the DIFC licence — removed entirely from the
        // .ae build (pages never generated, pruned from nav/footer/cards, 404 to
        // any visitor or bot, absent from both sitemaps).
        disabledSolutions: [
            '/solutions/global-property-investing/',
            '/solutions/citizenship-residency/',
            '/solutions/hnwi-services/',
        ],
        // Remove the Insurance landing but keep Investment-Linked Insurance, which
        // is promoted into the Insurance slot across nav, footer and cards.
        disabledExact: [
            '/solutions/insurance/',
        ],
        promotions: {
            '/solutions/insurance/': {
                path: '/solutions/insurance/investment-linked-insurance/',
                name: 'Investment-Linked Insurance',
            },
        },
    },
};

const config = { ...shared, ...sites[SITE], SITE };
// Bare host for display in copy (e.g. legal pages): 'rscapitalglobal.com' | '.ae'.
config.domain = config.siteUrl.replace(/^https?:\/\//, '');

/* Is this path offered on the current site? Disabled if it exactly matches an
   entry in disabledExact, or exactly matches / sits beneath an entry in
   disabledSolutions. Used to prune nav/footer/cards and drop pages at build. */
function isPathEnabled(path) {
    if (!path) return true;
    if ((config.disabledExact || []).includes(path)) return false;
    return !config.disabledSolutions.some(
        (p) => path === p || path.startsWith(p)
    );
}

/* Resolve a solution nav/card/list entry for the current site:
   - still offered            → the entry unchanged
   - removed but promoted      → the entry with the promoted path + name
   - removed with no promotion  → null (caller omits it) */
function resolveSolutionSlot(entry) {
    if (isPathEnabled(entry.path)) return entry;
    const promo = (config.promotions || {})[entry.path];
    return promo ? { ...entry, ...promo } : null;
}

// Language-region advertised per site for hreflang. Both sites are English;
// hreflang differentiates by region, with x-default falling back to the .com.
const HREFLANG = { com: 'en', ae: 'en-ae' };

// Does `path` exist on a given site build? Mirrors isPathEnabled but for any
// site key, so one build can reason about the other (needed for hreflang).
function pathExistsOn(path, key) {
    const s = sites[key];
    if (!s) return false;
    if (!path) return true;
    if ((s.disabledExact || []).includes(path)) return false;
    return !(s.disabledSolutions || []).some((p) => path === p || path.startsWith(p));
}

/* hreflang alternates for a path. Emitted only when the path exists on more than
   one site — a single-region page needs none. Both builds produce the SAME
   cluster, so the annotations are reciprocal (a hard hreflang requirement).
   x-default points at the international (.com) site. */
function hreflangAlternates(pathname) {
    if (!pathname) return [];
    const present = Object.keys(sites).filter((k) => pathExistsOn(pathname, k));
    if (present.length < 2) return [];
    const out = present.map((k) => ({ hreflang: HREFLANG[k], href: `${sites[k].siteUrl}${pathname}` }));
    out.push({ hreflang: 'x-default', href: `${sites.com.siteUrl}${pathname}` });
    return out;
}

/* Does an article show on a given site, from its Contentful "Markets" tags?
   A site with no market (.com) shows everything; a regional site shows only
   articles whose tags include its market value. */
function articleShowsOn(markets, key) {
    const market = sites[key].articleMarket;
    if (!market) return true;
    return Array.isArray(markets) && markets.includes(market);
}

/* Does an article show on the CURRENT site? */
function articleShows(markets) {
    return articleShowsOn(markets, config.SITE);
}

/* hreflang alternates for an article, from its "Markets" tags. Emitted only when
   the article shows on more than one site (single-region articles need none).
   Both builds produce the same cluster, so it stays reciprocal. */
function articleAlternates(pathname, markets) {
    if (!pathname) return [];
    const present = Object.keys(sites).filter((k) => articleShowsOn(markets, k));
    if (present.length < 2) return [];
    const out = present.map((k) => ({ hreflang: HREFLANG[k], href: `${sites[k].siteUrl}${pathname}` }));
    const def = present.includes('com') ? 'com' : present[0];
    out.push({ hreflang: 'x-default', href: `${sites[def].siteUrl}${pathname}` });
    return out;
}

module.exports = config;
module.exports.SITE = SITE;
module.exports.sites = sites;
module.exports.articleShows = articleShows;
module.exports.articleAlternates = articleAlternates;
module.exports.isPathEnabled = isPathEnabled;
module.exports.resolveSolutionSlot = resolveSolutionSlot;
module.exports.pathExistsOn = pathExistsOn;
module.exports.hreflangAlternates = hreflangAlternates;
